// "All off" for a WHOLE ROOM — the button you press on the way out.
//
// Asked for 2026-10-26 (the owner): "in room level there should be button to
// all off, which will help to close all lights while leaving room."
//
// A device already has its own All Off (the AllBar on a switch panel); this is
// the room's, so leaving does not mean opening four screens.
//
// ⚠️ IT DECIDES PER TYPE, AND IT REFUSES MORE THAN IT SENDS. "Everything off"
// in a house is not a loop over outputs:
//
//   switch / fan panels   off. A fan's off is SPEED 10, never 0
//                         (statusFormat.FAN_OFF) — `<n>-0` on a fan channel is
//                         not off, it is an invalid speed.
//   white / colour lights their own Off PRESET from core/devices/light, so the
//                         channel count and order are the product's, not ours.
//   a door lock           ⚠️ NEVER. A bulk action must not touch a lock, in
//                         either direction. Leaving a room is not a reason to
//                         unlock a door, and "off" has no meaning on one.
//   a curtain / gate      skipped. There is no "off" — the relays are momentary
//                         and closing on the way out is a different intention
//                         (and a timed run). Ask for it separately if it is
//                         wanted.
//   an IR blaster         its RF-light all-off (`LIGHT_KEYS.allOff` =
//                         `RTAlloff`, mixed case and case-SENSITIVE), because
//                         that is literally "all lights off" — the words this
//                         button exists for. ⚠️ The AC is NOT included: `ACOFF`
//                         is a separate token and switching somebody's air
//                         conditioner off is a bigger decision than the lights,
//                         so it is the dialog's opt-in (`includeAc`).
//                         ⚠️ And a blaster transmits and never reports, so
//                         nothing can confirm it arrived.
//   sensors, hub, meters  nothing to switch.
//
// ⚠️ AND IT HONOURS THE ROOM'S KEEP-ON LIST. The room-empty auto-off already
// lets an installer name outputs that must never be switched off — a fridge, a
// router, CCTV (`VacancyWatch.KeepOn`, `<uno>/<output>` keys). A button that
// ignored that list would switch off the very things the other feature exists
// to protect. With anything kept on, the panel's single `alloff` is unusable
// (it would take the kept output with it) and the plan goes per output instead.
//
// Pure: devices in, messages out. The publishing — one at a time, with a gap,
// because a device drops a command sent right behind another — is the caller's.

import { deviceTypeKey, relayCount, fanCount } from './deviceMeta';
import { FAN_OFF } from './statusFormat';
import { WWH_PRESETS, RGB_PRESETS, WWRGB_PRESETS } from './light';
import { LIGHT_KEYS, AC_KEYS } from './irRemote';

const str = (v) => String(v ?? '').trim();
const unoOf = (d) => str(d?.UniqueNumber ?? d?.uniqueNumber);
const idOf = (d) => Number(d?.DeviceID ?? d?.DeviceId ?? 0);
const nameOf = (d) => str(d?.DeviceName) || unoOf(d);

/** The keep-on CSV (`<uno>/<output>,…`) -> a Set of its keys. */
export const keepSet = (csv) => new Set(
  String(csv || '').split(',').map((s) => s.trim()).filter(Boolean),
);

const keepKey = (uno, output) => `${uno}/${output}`;

// The Off preset each light type already ships with — never a hand-written
// channel string, so a 5-channel light cannot be sent a 2-channel "off".
const LIGHT_OFF = {
  wwh: WWH_PRESETS,
  rgb: RGB_PRESETS,
  wwrgb: WWRGB_PRESETS,
};
const offPresetFor = (key) => {
  const p = (LIGHT_OFF[key] || []).find((x) => String(x.label).trim().toLowerCase() === 'off');
  return p ? p.command : null;
};

// Why a device was left alone. Shown to the user, so it reads as a sentence.
const SKIP = {
  door: 'a lock is never switched by a room button',
  curtain: 'a curtain has no "off" — close it from its own screen',
  bleWwh: 'a Bluetooth lamp is driven from the lamp tree, not from here',
  hub: 'a hub has no outputs',
  sceneController: 'scene buttons have nothing to switch',
  singlePhase: 'a meter only reports',
  threePhase: 'a meter only reports',
  sensor: 'a sensor only reports',
  unsupported: 'no controls this app knows about',
};

/**
 * What to send to switch a room off.
 *
 * `devices` are the room's device rows (as the room list returns them).
 * `keepOn`  the room's keep-on CSV or an array of `<uno>/<output>` keys.
 * `includeAc` also send the IR blaster's `ACOFF` — the lights go off either
 *            way; this is the air conditioner, which is a separate decision.
 *
 * -> { messages: [{ uniqueNumber, message, label }],
 *      skipped:  [{ uniqueNumber, name, why }],
 *      kept:     [{ uniqueNumber, output, key }],
 *      counts:   { devices, messages, kept } }
 */
export function planRoomAllOff(devices, { keepOn = '', includeAc = false } = {}) {
  const keep = Array.isArray(keepOn) ? new Set(keepOn.map(String)) : keepSet(keepOn);
  const messages = [];
  const skipped = [];
  const kept = [];
  const touched = new Set();

  (devices || []).forEach((d) => {
    const uno = unoOf(d);
    const id = idOf(d);
    if (!uno || !id) return;
    const key = deviceTypeKey(id);
    const name = nameOf(d);

    // ── Switch / fan panels ──────────────────────────────────────────────
    if (['oneTouch', 'twoTouch', 'fourTouch', 'touch', 'switchFan', 'fan'].includes(key)) {
      const relays = Math.max(0, relayCount(id) || 0);
      const fans = Math.max(0, fanCount(id) || 0);
      const keptHere = [];
      for (let o = 1; o <= relays + fans; o += 1) {
        if (keep.has(keepKey(uno, o))) keptHere.push(o);
      }
      keptHere.forEach((o) => kept.push({ uniqueNumber: uno, output: o, key: keepKey(uno, o) }));

      if (!keptHere.length) {
        // One message for the whole panel — what the device's own All Off bar
        // sends (useRelayStatus.setAll), lowercase as the MQTT path uses.
        messages.push({ uniqueNumber: uno, message: 'alloff', label: `${name} — all off` });
        // ⚠️ A FAN IS NOT COVERED BY A RELAY WORD. Its off is a SPEED, so it
        // gets its own message whatever `alloff` does to the switches.
        for (let f = 0; f < fans; f += 1) {
          messages.push({
            uniqueNumber: uno,
            message: `${relays + f + 1}-${FAN_OFF}`,
            label: `${name} — fan ${fans > 1 ? f + 1 : ''}off`.replace('  ', ' '),
          });
        }
      } else {
        // Something is kept on, so the panel-wide word would take it with it.
        for (let o = 1; o <= relays; o += 1) {
          if (!keep.has(keepKey(uno, o))) {
            messages.push({ uniqueNumber: uno, message: `${o}-0`, label: `${name} — ${o} off` });
          }
        }
        for (let f = 0; f < fans; f += 1) {
          const o = relays + f + 1;
          if (!keep.has(keepKey(uno, o))) {
            messages.push({ uniqueNumber: uno, message: `${o}-${FAN_OFF}`, label: `${name} — fan off` });
          }
        }
      }
      if (messages.length) touched.add(uno);
      return;
    }

    // ── Lights ───────────────────────────────────────────────────────────
    if (LIGHT_OFF[key]) {
      const off = offPresetFor(key);
      if (!off) { skipped.push({ uniqueNumber: uno, name, why: 'this light has no Off preset' }); return; }
      messages.push({ uniqueNumber: uno, message: off, label: `${name} — off` });
      touched.add(uno);
      return;
    }

    // ── The IR blaster: its lights always, the AC only when asked ────────
    if (key === 'irBlaster') {
      messages.push({ uniqueNumber: uno, message: LIGHT_KEYS.allOff, label: `${name} — lights off` });
      if (includeAc) {
        messages.push({ uniqueNumber: uno, message: AC_KEYS.off, label: `${name} — AC off` });
      }
      touched.add(uno);
      return;
    }

    skipped.push({ uniqueNumber: uno, name, why: SKIP[key] || SKIP.unsupported });
  });

  return {
    messages,
    skipped,
    kept,
    counts: { devices: touched.size, messages: messages.length, kept: kept.length },
  };
}

/** "3 devices · 5 commands · 1 kept on" — shown BEFORE anything is sent. */
export function allOffSummary(plan) {
  const c = (plan && plan.counts) || { devices: 0, messages: 0, kept: 0 };
  if (!c.messages) return 'nothing in this room can be switched off';
  const bits = [`${c.devices} device${c.devices === 1 ? '' : 's'}`];
  bits.push(`${c.messages} command${c.messages === 1 ? '' : 's'}`);
  if (c.kept) bits.push(`${c.kept} kept on`);
  return bits.join(' · ');
}

const roomAllOff = { planRoomAllOff, allOffSummary, keepSet };
export default roomAllOff;
